import React, { useState, useMemo } from 'react';
import { Box, Card, CardContent, Typography, Chip, TextField, Select, MenuItem, FormControl, InputLabel, Paper, Button, Rating, Avatar, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { AccountTree, Person, Star, FilterList, CheckCircle, Cancel, ExpandMore } from '@mui/icons-material';
import ReactFlow, { 
  Controls, 
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle
} from 'reactflow';
import 'reactflow/dist/style.css';

const CommitteeArchitecture = () => {
  const [selectedArea, setSelectedArea] = useState('all');
  const [selectedSubcommittee, setSelectedSubcommittee] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [minRating, setMinRating] = useState(0);

  const committeeData = {
    agricola: {
      name: 'Comité Agrícola',
      rating: 4.5,
      subcommittees: [
        {
          name: 'Agricultura Sostenible',
          managers: [
            { id: 1, name: 'Juan Pérez', profession: 'Ing. Agrónomo', projects: 12, rating: 4.8, available: true },
            { id: 2, name: 'María García', profession: 'Ing. Ambiental', projects: 8, rating: 4.6, available: true }
          ]
        },
        {
          name: 'Ganadería',
          managers: [
            { id: 3, name: 'Carlos Ruiz', profession: 'Veterinario', projects: 15, rating: 4.7, available: false },
            { id: 4, name: 'Ana López', profession: 'Zootecnista', projects: 10, rating: 4.5, available: true }
          ]
        }
      ]
    },
    salud: {
      name: 'Comité Salud',
      rating: 4.7,
      subcommittees: [
        {
          name: 'Belleza',
          managers: [
            { id: 5, name: 'Laura Martínez', profession: 'Dermatóloga', projects: 20, rating: 4.9, available: true },
            { id: 6, name: 'Pedro Sánchez', profession: 'Cirujano Plástico', projects: 18, rating: 4.8, available: true }
          ]
        },
        {
          name: 'Odontología',
          managers: [
            { id: 7, name: 'Sofia Torres', profession: 'Odontóloga', projects: 14, rating: 4.6, available: true },
            { id: 8, name: 'Diego Ramírez', profession: 'Ortodoncista', projects: 11, rating: 4.4, available: false }
          ]
        },
        {
          name: 'Salud General',
          managers: [
            { id: 9, name: 'Carmen Díaz', profession: 'Médica General', projects: 25, rating: 4.9, available: true },
            { id: 10, name: 'Roberto Gómez', profession: 'Enfermero', projects: 16, rating: 4.5, available: true }
          ]
        }
      ]
    },
    infraestructura: {
      name: 'Comité Infraestructura',
      rating: 4.4,
      subcommittees: [
        {
          name: 'Vías y Transporte',
          managers: [
            { id: 11, name: 'Luis Hernández', profession: 'Ing. Civil', projects: 22, rating: 4.7, available: true }
          ]
        },
        {
          name: 'Edificaciones',
          managers: [
            { id: 12, name: 'Patricia Vargas', profession: 'Arquitecta', projects: 19, rating: 4.8, available: true }
          ]
        }
      ]
    }
  };

  const getAllManagers = () => {
    let managers = [];
    Object.entries(committeeData).forEach(([key, committee]) => {
      committee.subcommittees.forEach(sub => {
        sub.managers.forEach(manager => {
          managers.push({
            ...manager,
            committee: committee.name,
            subcommittee: sub.name,
            area: key
          });
        });
      });
    });
    return managers;
  };

  const getAvailableSubcommittees = () => {
    if (selectedArea === 'all') return [];
    return committeeData[selectedArea]?.subcommittees || [];
  };

  const handleSubcommitteeClick = (area, subcommitteeName) => {
    setSelectedArea(area);
    setSelectedSubcommittee(subcommitteeName);
  };

  const handleAreaChange = (newArea) => {
    setSelectedArea(newArea);
    setSelectedSubcommittee('all');
  };

  const filteredManagers = getAllManagers().filter(manager => {
    const matchesArea = selectedArea === 'all' || manager.area === selectedArea;
    const matchesSubcommittee = selectedSubcommittee === 'all' || manager.subcommittee === selectedSubcommittee;
    const matchesSearch = manager.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          manager.profession.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRating = manager.rating >= minRating;
    return matchesArea && matchesSubcommittee && matchesSearch && matchesRating;
  });

  // Componentes de nodos personalizados con Handles
  const RootNode = ({ data }) => (
    <>
      <Card sx={{ 
        width: 200,
        bgcolor: 'secondary.main',
        color: 'white',
        boxShadow: 3
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
            {data.label}
          </Typography>
        </CardContent>
      </Card>
      <Handle type="source" position={Position.Bottom} style={{ background: '#1976d2' }} />
    </>
  );

  const CommitteeNode = ({ data }) => (
    <>
      <Handle type="target" position={Position.Top} style={{ background: '#1976d2' }} />
      <Card sx={{ 
        width: 180,
        bgcolor: 'primary.main',
        color: 'white',
        boxShadow: 2
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, justifyContent: 'center' }}>
            <AccountTree sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight="bold">
              {data.label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'center' }}>
            <Star sx={{ color: 'gold', fontSize: 14 }} />
            <Typography variant="caption" fontWeight="bold">
              {data.rating}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Handle type="source" position={Position.Bottom} style={{ background: '#1976d2' }} />
    </>
  );

  const SubcommitteeNode = ({ data }) => (
    <>
      <Handle type="target" position={Position.Top} style={{ background: '#1976d2' }} />
      <Card sx={{ 
        width: 160,
        bgcolor: 'primary.50',
        border: '2px solid',
        borderColor: 'primary.main',
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="caption" fontWeight="bold" color="primary" textAlign="center" display="block">
            {data.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ fontSize: '0.65rem' }}>
            {data.count} gestores
          </Typography>
        </CardContent>
      </Card>
      <Handle type="source" position={Position.Bottom} style={{ background: '#1976d2' }} />
    </>
  );

  const ManagerNode = ({ data }) => (
    <>
      <Handle type="target" position={Position.Top} style={{ background: '#1976d2' }} />
      <Card sx={{ 
        width: 150,
        bgcolor: data.available ? 'white' : 'grey.100',
        border: '2px solid',
        borderColor: data.available ? 'success.main' : 'grey.400',
        boxShadow: 1
      }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.7rem' }}>
              {data.label.charAt(0)}
            </Avatar>
            {data.available ? 
              <CheckCircle color="success" sx={{ fontSize: 16 }} /> : 
              <Cancel color="error" sx={{ fontSize: 16 }} />
            }
          </Box>
          <Typography variant="caption" fontWeight="bold" display="block" sx={{ fontSize: '0.75rem' }}>
            {data.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
            {data.profession}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.5 }}>
            <Star sx={{ fontSize: 11, color: 'gold' }} />
            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
              {data.rating}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </>
  );

  const nodeTypes = useMemo(() => ({
    root: RootNode,
    committee: CommitteeNode,
    subcommittee: SubcommitteeNode,
    manager: ManagerNode,
  }), []);

  // Construir nodos y edges con SISTEMA DE MÚLTIPLES FILAS
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes = [];
    const edges = [];
    
    const dataToShow = selectedArea === 'all' ? 
      Object.entries(committeeData) : 
      [[selectedArea, committeeData[selectedArea]]];

    // Dimensiones y espaciado
    const ROOT_WIDTH = 200;
    const COMMITTEE_WIDTH = 180;
    const SUBCOMMITTEE_WIDTH = 160;
    const MANAGER_WIDTH = 150;
    const HORIZONTAL_SPACING = 60; // Aumentado para más espacio
    const VERTICAL_SPACING = 130;
    const MANAGER_ROW_SPACING = 100;
    const MAX_MANAGERS_PER_ROW = 3;
    const SUBCOMMITTEE_SPACING = 80; // Espacio extra entre subcomités
    
    // Niveles Y
    const ROOT_Y = 50;
    const COMMITTEE_Y = ROOT_Y + VERTICAL_SPACING;
    const SUBCOMMITTEE_Y = COMMITTEE_Y + VERTICAL_SPACING;
    const MANAGER_Y_START = SUBCOMMITTEE_Y + VERTICAL_SPACING;

    // Calcular anchos
    let totalWidth = 0;
    const committeeWidths = [];
    
    dataToShow.forEach(([key, committee]) => {
      let maxSubcommitteeRowWidth = 0;
      
      committee.subcommittees.forEach(sub => {
        const managersPerRow = Math.min(sub.managers.length, MAX_MANAGERS_PER_ROW);
        const managersWidth = (managersPerRow * MANAGER_WIDTH) + ((managersPerRow - 1) * HORIZONTAL_SPACING);
        maxSubcommitteeRowWidth = Math.max(maxSubcommitteeRowWidth, managersWidth);
      });
      
      const subcommitteesWidth = (committee.subcommittees.length * SUBCOMMITTEE_WIDTH) + ((committee.subcommittees.length - 1) * SUBCOMMITTEE_SPACING);
      const committeeWidth = Math.max(maxSubcommitteeRowWidth, subcommitteesWidth, COMMITTEE_WIDTH) + 100; // Margen extra
      
      committeeWidths.push(committeeWidth);
      totalWidth += committeeWidth;
    });
    
    totalWidth += (dataToShow.length - 1) * 150; // Mayor separación entre comités

    // Nodo raíz
    const rootX = (totalWidth / 2) - (ROOT_WIDTH / 2);
    nodes.push({
      id: 'root',
      type: 'root',
      data: { label: 'Sistema de Comités' },
      position: { x: rootX, y: ROOT_Y },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    });

    let currentX = 0;

    dataToShow.forEach(([key, committee], cIdx) => {
      const committeeId = `committee-${key}`;
      const committeeWidth = committeeWidths[cIdx];
      const committeeX = currentX + (committeeWidth / 2) - (COMMITTEE_WIDTH / 2);

      // Nodo comité
      nodes.push({
        id: committeeId,
        type: 'committee',
        data: { label: committee.name, rating: committee.rating },
        position: { x: committeeX, y: COMMITTEE_Y },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      });

      edges.push({
        id: `e-root-${committeeId}`,
        source: 'root',
        target: committeeId,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#1976d2', strokeWidth: 3 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#1976d2',
          width: 20,
          height: 20
        },
      });

      const subCount = committee.subcommittees.length;
      const totalSubWidth = (subCount * SUBCOMMITTEE_WIDTH) + ((subCount - 1) * SUBCOMMITTEE_SPACING);
      const subStartX = currentX + (committeeWidth / 2) - (totalSubWidth / 2);

      committee.subcommittees.forEach((sub, sIdx) => {
        const subId = `sub-${key}-${sIdx}`;
        const subX = subStartX + (sIdx * (SUBCOMMITTEE_WIDTH + SUBCOMMITTEE_SPACING));

        nodes.push({
          id: subId,
          type: 'subcommittee',
          data: { label: sub.name, count: sub.managers.length },
          position: { x: subX, y: SUBCOMMITTEE_Y },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });

        edges.push({
          id: `e-${committeeId}-${subId}`,
          source: committeeId,
          target: subId,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#1976d2', strokeWidth: 3 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#1976d2',
            width: 20,
            height: 20
          },
        });

        const managerCount = sub.managers.length;
        
        // SISTEMA DE MÚLTIPLES FILAS - CLAVE AQUÍ
        sub.managers.forEach((manager, mIdx) => {
          const managerId = `manager-${manager.id}`;
          
          // Calcular fila y columna
          const row = Math.floor(mIdx / MAX_MANAGERS_PER_ROW);
          const col = mIdx % MAX_MANAGERS_PER_ROW;
          const managersInThisRow = Math.min(MAX_MANAGERS_PER_ROW, managerCount - (row * MAX_MANAGERS_PER_ROW));
          
          // Calcular X centrado para esta fila
          const rowWidth = (managersInThisRow * MANAGER_WIDTH) + ((managersInThisRow - 1) * HORIZONTAL_SPACING);
          const rowStartX = subX + (SUBCOMMITTEE_WIDTH / 2) - (rowWidth / 2);
          const managerX = rowStartX + (col * (MANAGER_WIDTH + HORIZONTAL_SPACING));
          
          // Calcular Y según la fila
          const managerY = MANAGER_Y_START + (row * MANAGER_ROW_SPACING);

          nodes.push({
            id: managerId,
            type: 'manager',
            data: { 
              label: manager.name, 
              profession: manager.profession,
              rating: manager.rating,
              available: manager.available
            },
            position: { x: managerX, y: managerY },
            targetPosition: Position.Top,
            sourcePosition: Position.Bottom,
          });

          edges.push({
            id: `e-${subId}-${managerId}`,
            source: subId,
            target: managerId,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#1976d2', strokeWidth: 3 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#1976d2',
              width: 20,
              height: 20
            },
          });
        });
      });

      currentX += committeeWidth + 150; // Mayor espacio entre comités
    });

    return { nodes, edges };
  }, [selectedArea]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  return (
    <Box sx={{ p: 4, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Sistema de Gestión por Comités
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Asignación de gestores especializados según el área del proyecto
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList />
          <Typography variant="h6">Filtros de Búsqueda</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Área</InputLabel>
            <Select
              value={selectedArea}
              onChange={(e) => handleAreaChange(e.target.value)}
              label="Área"
            >
              <MenuItem value="all">Todas las áreas</MenuItem>
              <MenuItem value="agricola">Agrícola</MenuItem>
              <MenuItem value="salud">Salud</MenuItem>
              <MenuItem value="infraestructura">Infraestructura</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={selectedArea === 'all'}>
            <InputLabel>Subcomité</InputLabel>
            <Select
              value={selectedSubcommittee}
              onChange={(e) => setSelectedSubcommittee(e.target.value)}
              label="Subcomité"
            >
              <MenuItem value="all">Todos los subcomités</MenuItem>
              {getAvailableSubcommittees().map((sub, idx) => (
                <MenuItem key={idx} value={sub.name}>{sub.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Buscar gestor o profesión"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FormControl fullWidth>
            <InputLabel>Calificación mínima</InputLabel>
            <Select
              value={minRating}
              onChange={(e) => setMinRating(e.target.value)}
              label="Calificación mínima"
            >
              <MenuItem value={0}>Todas</MenuItem>
              <MenuItem value={4}>4+ estrellas</MenuItem>
              <MenuItem value={4.5}>4.5+ estrellas</MenuItem>
              <MenuItem value={4.8}>4.8+ estrellas</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Organigrama COLAPSABLE CON ACCORDION - INICIA CERRADO */}
      <Accordion sx={{ mb: 4 }}>
        <AccordionSummary 
          expandIcon={<ExpandMore />}
          sx={{ 
            bgcolor: 'primary.50',
            '&:hover': { bgcolor: 'primary.100' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTree sx={{ color: 'primary.main' }} />
            <Typography variant="h5" fontWeight="bold">
              Organigrama Completo
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <Box sx={{ height: '800px', bgcolor: 'grey.50', borderRadius: 2 }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.15, minZoom: 0.4, maxZoom: 1 }}
              attributionPosition="bottom-left"
              minZoom={0.2}
              maxZoom={1.5}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#aaa" gap={16} />
              <Controls />
            </ReactFlow>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Vista Jerárquica de Comités */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mb={2}>
        Estructura Organizacional
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
        {Object.entries(committeeData).map(([key, committee]) => (
          <Card key={key} sx={{ 
            border: selectedArea === key ? '2px solid' : 'none',
            borderColor: 'primary.main'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountTree color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {committee.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={committee.rating} readOnly precision={0.1} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {committee.rating}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Subcomités:
              </Typography>
              {committee.subcommittees.map((sub, idx) => (
                <Chip
                  key={idx}
                  label={`${sub.name} (${sub.managers.length})`}
                  size="small"
                  sx={{ m: 0.5, cursor: 'pointer' }}
                  color="primary"
                  variant={selectedSubcommittee === sub.name ? "filled" : "outlined"}
                  onClick={() => handleSubcommitteeClick(key, sub.name)}
                />
              ))}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Lista de Gestores Filtrados */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mb={2}>
        Gestores Disponibles ({filteredManagers.filter(m => m.available).length})
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        {filteredManagers.map((manager) => (
          <Card 
            key={manager.id}
            sx={{ 
              opacity: manager.available ? 1 : 0.6,
              border: manager.available ? 'none' : '1px dashed',
              borderColor: 'grey.400'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Person color={manager.available ? 'primary' : 'disabled'} />
                    <Typography variant="h6" fontWeight="bold">
                      {manager.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {manager.profession}
                  </Typography>
                </Box>
                <Chip 
                  label={manager.available ? 'Disponible' : 'Ocupado'} 
                  color={manager.available ? 'success' : 'default'}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Comité:</strong> {manager.committee} → {manager.subcommittee}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>Proyectos gestionados:</strong> {manager.projects}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={manager.rating} readOnly precision={0.1} size="small" />
                  <Typography variant="caption" fontWeight="bold">
                    {manager.rating}
                  </Typography>
                </Box>
              </Box>

              {manager.available && (
                <Button 
                  variant="contained" 
                  fullWidth
                  size="small"
                >
                  Asignar al Proyecto
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default CommitteeArchitecture;